'use client';
import { useDebounceFn, useSize } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { BaseLineReplace } from '@/app/components/icons/BaseLineReplace';
import { BaseLineReset } from '@/app/components/icons/BaseLineReset';
import { Loading } from '@/app/components/icons/Loading';
import { ProductLineElement } from '@/app/components/icons/ProductLineElement';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { ImageUploader } from '@/app/components/shared/ImageUploader';
import { MultiImageGrid } from '@/app/components/shared/multi-image-grid';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/app/components/ui/dialog';
import { MAX_REFERENCE_PROMPTS, UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import { useDragAndDrop, useGlobalModal, useIsScreen } from '@/app/hooks';
import { useStoreApi } from '@/app/providers/store';
import type {
  ChangedImagePrompt,
  FormImagePrompt,
  ImagePrompt,
  ImagePromptWithSize,
  ImageRange,
  ImageResolution,
  ImageUploadFile,
  ReferencePrompt,
  UploadImageType,
} from '@/app/types';
import { cn, convertComponents2Assets } from '@/app/utils';
import { resizeSourceImage } from '@/app/utils/client';
import { CropOptionDisplay } from './components/CropOptionDisplay';
import { ImageTooltipContent } from './components/ImageTooltipContent';
import { LoadingOverlay } from './components/LoadingOverlay';
import { ThumbContent } from './components/ThumbContent';
import useClipMask from './use-clip-mask';
import useCropOptions from './use-crop-options';
import { calculateRangeAndScale, getNewRange, TIME_OUT } from './utils';

type CharImgCropModalProps = {
  title?: string;
  description?: string;
  onCancel: () => void; //点击取消按钮
  onConfirm: (changedImagePrompts: ChangedImagePrompt[]) => Promise<void>;
  imageSource: ImagePromptWithSize | null; //图片信息
  imagePrompts?: (FormImagePrompt | null)[]; // 图片列表
  referencePrompts?: ReferencePrompt[]; // 图片+主体列表
  handleClickImagePrompt: (idx: number, imagePrompt?: ImagePrompt | null) => Promise<void>; //点击图片缩略图
  handleRemoveImage?: (image: ImagePrompt, idx: number) => void; //删除图片
  handleRemoveReference?: (id: string) => void; //删除主体或图片
  maxImageSize: number; // 图片最大尺寸
  scene?: UploadScene; // 上传场景
  onUploadImage?: (file: ImageUploadFile, onUploadStatusChange?: (status: ImageUploadFile['status']) => void) => void; //上传图片
  replaceImage: (index: number, file: ImageUploadFile) => UploadImageType | null; // 替换图片
  handleExchange?: (fromIndex: number, toIndex: number) => void; // 图片换位
  maxImageAndElementCount?: number;
  allowTempMode?: boolean; // 是否允许临时模式（未登录时不上传，只保存临时图片）
};

//最初设计在参考生中使用，后也用于主体上传的图片裁剪
export const CharImgCropModal: FC<CharImgCropModalProps> = ({
  title,
  description,
  onCancel,
  onConfirm,
  handleClickImagePrompt,
  referencePrompts,
  imagePrompts,
  imageSource,
  handleRemoveImage,
  handleRemoveReference,
  maxImageSize,
  scene = UPLOAD_SCENE.VIDU,
  onUploadImage,
  replaceImage,
  handleExchange,
  maxImageAndElementCount = MAX_REFERENCE_PROMPTS,
  allowTempMode = false,
}) => {
  const [range, setRange] = useState<ImageRange>([0, 0, 0, 0]); // 裁剪区域（虚线框）
  const [saving, setSaving] = useState(false); // 是否正在保存
  const [imageLoaded, setImageLoaded] = useState(false); // 图片是否已加载完成
  const [selectedAspect, setSelectedAspect] = useState<'free' | number>('free'); //当前选中比例
  const [activeElementId, setActiveElementId] = useState<string>(''); // 当前选中的主体id
  const [changedImagePrompts, setChangedImagePrompts] = useState<ChangedImagePrompt[]>([]); //缓存已修改的图片
  const maxImageRange = useRef<ImageRange>([0, 0, 0, 0]); // 裁剪最大区域（不能超过图片范围）
  const resizeScale = useRef<number>(1); // 图片的缩放比例

  const { isSmScreen } = useIsScreen();
  const t = useTranslations();
  const { freeAspectOption, defaultCropOptions } = useCropOptions();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const store = useStoreApi();
  const imgRef = useRef<HTMLImageElement>(null); // 图片的DOM节点
  const imgSize = useSize(imgRef); // 获取图片实际渲染尺寸

  // 图片 DOM 实际渲染尺寸（从 imgSize 派生）
  const domResolution: ImageResolution = imgSize ?? { width: 0, height: 0 };

  const aspectRatios = [freeAspectOption, ...defaultCropOptions]; //固定的裁剪选项

  // 通过源图片地址获取图片id
  const getImagePromptId = useCallback(
    (prompt: ImagePrompt): string => {
      if (activeElementId) return ''; //选中主体时，返回空字符串
      for (const imagePrompt of imagePrompts || []) {
        if (imagePrompt?.prompt?.sourceImage === prompt.sourceImage) {
          return imagePrompt.id;
        }
      }
      for (const referencePrompt of referencePrompts || []) {
        if (referencePrompt.type === 'image' && referencePrompt.prompt?.sourceImage === prompt.sourceImage) {
          return referencePrompt.id;
        }
      }
      return '';
    },
    [imagePrompts, referencePrompts, activeElementId],
  );

  // 当前选中的图片id
  const activeImageId = useMemo(() => {
    return imageSource ? getImagePromptId(imageSource) : '';
  }, [imageSource, getImagePromptId]);

  // 当前选中的图片
  const activeImageIndex = useMemo<number>(() => {
    // 先在 imagePrompts 中查找
    const imagePromptsIndex = imagePrompts?.findIndex((it) => it?.id === activeImageId) ?? -1;
    if (imagePromptsIndex >= 0) {
      return imagePromptsIndex;
    }

    // 再在 referencePrompts 中查找
    const referencePromptsIndex = referencePrompts?.findIndex((it) => it?.id === activeImageId) ?? -1;
    if (referencePromptsIndex >= 0) {
      return referencePromptsIndex;
    }

    return -1; // 都没找到，返回默认值
  }, [imagePrompts, referencePrompts, activeImageId]);

  //小屏时重置为自由比例，且不展示缩略图和比例选项
  useEffect(() => {
    isSmScreen && handleAspectChange('free');
  }, [isSmScreen]);

  //图片源变化时，重置图片加载标志位并更新比例
  useEffect(() => {
    setImageLoaded(false); //只要图片变化，就清空图片加载状态
    if (imageSource) {
      // 当图片源变化时，恢复比例
      const targetChangedImagePrompt = changedImagePrompts.find((it) => it.id === activeImageId);
      if (targetChangedImagePrompt?.cropRatio) {
        //先取缓存
        setSelectedAspect(targetChangedImagePrompt?.cropRatio);
      } else if (imageSource.cropRatio !== undefined) {
        // 没有缓存，则使用图片本身的裁剪比例
        setSelectedAspect(imageSource.cropRatio);
      } else {
        // 没有缓存，也没有图片本身的裁剪比例，则使用自由比例
        setSelectedAspect('free');
      }
    } else {
      //imageSource 无值时，弹窗关闭
      setChangedImagePrompts([]); // 清空缓存
      setActiveElementId(''); // 清空选中主体id
    }
  }, [imageSource]); //此处只放 imageSource，原因：aspectRatios 不变， changedImagePrompts 提前缓存，activeImageId 变化时机同步

  //图片加载成功后，更新裁剪区域，并记录图片的缩放比例和裁剪最大区域
  useEffect(() => {
    if (!imageLoaded || !imageSource || !imgSize) return;

    const { width, height } = imgSize; //获取img标签的宽高
    const { sourceImageWidth, sourceImageHeight } = imageSource; //获取图片的原始宽高
    const [x1, y1, x2, y2] = imageSource.selectedImageRange ?? [0, 0, sourceImageWidth, sourceImageHeight]; //获取图片的裁剪区域（如果没有则默认全图）
    const { scale, range } = calculateRangeAndScale(
      {
        width: sourceImageWidth,
        height: sourceImageHeight,
      },
      {
        width,
        height,
      },
    );
    resizeScale.current = scale; // 图片的缩放比例
    maxImageRange.current = range; // 裁剪最大区域（图片范围）
    const targetChangedImagePrompt = changedImagePrompts.find((it) => it.id === activeImageId);
    if (targetChangedImagePrompt?.range) {
      setRange(targetChangedImagePrompt.range); // 使用缓存的显示坐标
    } else {
      setRange([
        x1 * resizeScale.current + maxImageRange.current[0],
        y1 * resizeScale.current + maxImageRange.current[1],
        x2 * resizeScale.current + maxImageRange.current[0],
        y2 * resizeScale.current + maxImageRange.current[1],
      ]);
    }
  }, [imageLoaded, imgSize]);

  const { run: updateCurrentImageCache } = useDebounceFn(
    () => {
      if (imageSource && activeImageId) {
        const changedImagePrompt: ChangedImagePrompt = {
          id: activeImageId,
          range,
          cropRatio: selectedAspect,
          imagePrompt: imageSource,
          containerSize: domResolution,
        };
        setChangedImagePrompts((prev) => [...prev.filter((it) => it.id !== activeImageId), changedImagePrompt]);
      }
    },
    {
      wait: TIME_OUT,
    },
  );

  // 裁剪区域变化时，更新当前图片的缓存
  useEffect(() => {
    updateCurrentImageCache();
  }, [range]);

  // 剪裁蒙层
  const { clipMaskDom, handleMoving, handleMoveEnd } = useClipMask({
    range,
    selectedAspect,
    maxImageRange,
    imageScale: resizeScale.current,
    setRange,
  });

  const clickImagePrompt = (idx: number, prompt: ImagePrompt) => {
    handleClickImagePrompt(idx, prompt);
    setActiveElementId('');
  };

  const handleSaveCropImage = async () => {
    setSaving(true);

    // 构造包含当前图片的完整缓存数据
    let allChangedImagePrompts = [...changedImagePrompts];
    if (imageSource && activeImageId) {
      const currentImagePrompt: ChangedImagePrompt = {
        id: activeImageId,
        range,
        cropRatio: selectedAspect,
        imagePrompt: imageSource,
        containerSize: domResolution,
      };
      allChangedImagePrompts = [...allChangedImagePrompts.filter((it) => it.id !== activeImageId), currentImagePrompt];
    }

    // 转换所有缓存数据的显示坐标为原图坐标
    const processedChangedImagePrompts = allChangedImagePrompts.map((changedPrompt) => {
      const { sourceImageWidth, sourceImageHeight } = changedPrompt.imagePrompt;
      if (!sourceImageWidth || !sourceImageHeight) return changedPrompt;

      // 使用该图片缓存时的容器尺寸进行计算
      const { scale, range: imageRange } = calculateRangeAndScale(
        { width: sourceImageWidth, height: sourceImageHeight },
        changedPrompt.containerSize,
      );

      // 将显示坐标转换为原图坐标
      const originalRange: ImageRange = [
        Math.floor((changedPrompt.range[0] - imageRange[0]) / scale),
        Math.floor((changedPrompt.range[1] - imageRange[1]) / scale),
        Math.floor((changedPrompt.range[2] - imageRange[0]) / scale),
        Math.floor((changedPrompt.range[3] - imageRange[1]) / scale),
      ];

      return {
        ...changedPrompt,
        range: originalRange,
      };
    });

    // 保存裁剪区域和选择的比例
    await onConfirm(processedChangedImagePrompts);
    setSaving(false);
    onCancel();
  };

  const handleAspectChange = (value: 'free' | number) => {
    if (activeElementId) return;
    setSelectedAspect(value);
    if (value === 'free') return; // 自由比例不改变裁剪区域
    setRange(getNewRange(range, value, maxImageRange.current));
  };

  // 选中主体时大屏幕的显示内容
  const renderActiveElementDom = () => {
    const activeElement = referencePrompts?.find((it) => it.id === activeElementId && it.type === 'element');
    if (activeElement && activeElement.type === 'element' && activeElement.prompt) {
      return (
        <div className="absolute inset-0 z-10 bg-black">
          <MultiImageGrid
            images={
              convertComponents2Assets(activeElement.prompt.components || [])
                ?.map((it) => {
                  if (!it || 'video' in it) return null;
                  return it.selectedImagePreview || it.sourceImagePreview;
                })
                .filter((url): url is string => url !== null) || []
            }
          />
        </div>
      );
    }
    return null;
  };

  const renderRemoveDom = (handleRemove: () => void, isLast: boolean, removeCallback?: () => void) => {
    return (
      <div className="absolute top-[-8px] right-[-8px] z-10 hidden items-center justify-center transition-opacity group-hover:block sm:group-hover:hidden">
        <div
          className="flex size-5 cursor-pointer items-center justify-center rounded-full bg-system-white24 text-system-white backdrop-blur-[2px]"
          onClick={(e) => {
            e.stopPropagation();
            if (isLast) {
              openConfirmDialog({
                title: t('imgCropModal.deleteLast.title'),
                content: t('imgCropModal.deleteLast.content'),
                primaryButtonText: t('imgCropModal.deleteLast.confirm'),
                secondaryButtonText: t('imgCropModal.deleteLast.cancel'),
                onPrimaryAction: () => {
                  handleRemove();
                  onCancel();
                },
              });
              return;
            }
            handleRemove();
            removeCallback?.(); // 删除后，选中下一个图片（需要视觉上与上一个删除的图片位置接近）
          }}
        >
          <BaseLineClose className="text-base" />
        </div>
      </div>
    );
  };

  const handleReset = () => {
    handleAspectChange('free');
    if (maxImageRange.current) setRange([...maxImageRange.current]); //选择整个图片
  };

  const { onDragStart, onDragOver, onDropCapture } = useDragAndDrop({ onReorder: handleExchange });
  const renderImageDom = (id: string, prompt: ImagePrompt, removeButton: ReactNode, idx: number, isActive: boolean) => {
    const promptId = getImagePromptId(prompt);

    // 获取当前图片的缓存数据
    const cachedData = changedImagePrompts.find((cache) => cache.id === promptId);

    // 获取当前图片应该使用的 range（仅当需要实时预览时使用）
    const imageRange: ImageRange | undefined = isActive ? range : cachedData?.range;

    // 非激活图片使用缓存的容器尺寸，避免切换时因 domResolution 重置为 0 导致闪烁
    const thumbDomResolution = isActive ? domResolution : cachedData?.containerSize;

    return (
      <CustomTooltip
        key={id}
        content={
          <ImageTooltipContent
            changedImagePrompts={changedImagePrompts}
            activeImageId={activeImageId}
            range={range}
            domResolution={domResolution}
            promptId={promptId}
            prompt={prompt}
          />
        }
        side="bottom"
        disableHoverableContent={true}
        trigger="hover"
      >
        <div
          onClick={() => clickImagePrompt(idx, prompt)}
          className={cn(
            'group relative h-16 w-16 cursor-pointer rounded-6 border-2 border-system-white',
            isActive && 'gradient-border-2 border-gradient-b-primary-dark',
          )}
          draggable={!!handleExchange}
          onDragStart={(e) => onDragStart(e, idx)}
          onDragOver={(e) => onDragOver(e)}
          onDropCapture={(e) => onDropCapture(e, idx)}
        >
          <ThumbContent isActive={isActive} prompt={prompt} range={imageRange} domResolution={thumbDomResolution} />

          {removeButton}

          {/* 拖动蒙层：防止拖动时变形 */}
          <div className="pointer-events-none absolute inset-0" />
        </div>
      </CustomTooltip>
    );
  };

  const selectElementTooltipContent = activeElementId ? t('imgCropModal.disabledTip') : false; //选择主体时候的提示

  return (
    <Dialog open={Boolean(imageSource)}>
      <DialogContent className="max-w-[822px] gap-1 md:h-4/5" variant="mobileFullScreen" closable={false}>
        <DialogTitle className="pl-4">{title ?? t('CharImgCropModal.title')}</DialogTitle>
        <div className="flex-1 md:flex md:flex-col md:overflow-hidden">
          <div className="flex items-center justify-between pr-4 pl-4">
            <DialogDescription>{description ?? t('CharImgCropModal.description')}</DialogDescription>

            <ImageUploader
              key="replace-image"
              maxCount={1}
              className="mb-3"
              scene={scene}
              getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
              onChange={(file) => {
                const newImage = replaceImage(activeImageIndex, file);
                if (newImage) {
                  // 清除被删替换图片的缓存：由于数据延迟更新特性，这里删除的是旧缓存，没问题
                  setChangedImagePrompts((prev) => prev.filter((it) => it.id !== activeImageId));
                  setImageLoaded(false);
                  clickImagePrompt(activeImageIndex, newImage);
                }
              }}
              allowTempMode={allowTempMode}
            >
              <CustomTooltip content={selectElementTooltipContent}>
                <Button
                  variant="outline"
                  className="h-10 border border-system-white24 bg-[#181C23] px-4 py-2 text-sm sm:h-8 md:px-5 md:text-xs"
                  disabled={Boolean(activeElementId)}
                >
                  <BaseLineReplace className="mr-2 text-lg" />
                  {t('imgCropModal.replace')}
                </Button>
              </CustomTooltip>
            </ImageUploader>
          </div>
          <div
            onPointerMove={handleMoving}
            onPointerLeave={handleMoveEnd}
            onPointerUp={handleMoveEnd}
            className="relative h-[400px] w-full select-none p-4 md:flex-1 md:overflow-hidden"
          >
            {imageSource?.sourceImagePreview ? (
              <img
                src={imageSource.sourceImagePreview}
                alt="image-source"
                id="crop-image-source"
                ref={imgRef}
                className="size-full object-contain"
                onLoad={() => setImageLoaded(true)}
              />
            ) : null}
            {clipMaskDom}
            {/* 图片加载中的 loading 状态 */}
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                <div className="flex flex-col items-center gap-3">
                  <Loading className="animate-spin text-4xl text-white" />
                </div>
              </div>
            )}
            {renderActiveElementDom()}
          </div>

          {/* 纯图片缩略图区域 */}
          {!isSmScreen && imagePrompts && imagePrompts.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
              {imagePrompts.map((imagePrompt, idx) => {
                if (!imagePrompt) return null;
                const total = imagePrompts.filter((it) => it).length;
                const { id, status, prompt } = imagePrompt;
                const selectNextImagePrompt = (reverse: boolean, deletedIndex?: number) => {
                  // 由于数据更新有延迟，手动过滤掉即将删除的图片，模拟删除后的状态
                  const currentImagePrompts = [...(imagePrompts || [])].map((item, index) =>
                    index === deletedIndex ? null : item,
                  );
                  if (reverse) currentImagePrompts.reverse(); // reverse 为 true 选择视觉上第2张，否则选择视觉第1张
                  let targetIndex = 0;
                  for (let i = 0; i < currentImagePrompts.length; i++) {
                    if (currentImagePrompts[i]) {
                      targetIndex = i;
                      break;
                    }
                  }
                  const targetPrompt = currentImagePrompts[targetIndex];
                  if (!targetPrompt || !targetPrompt.prompt) return;
                  if (reverse) targetIndex = currentImagePrompts.length - 1 - targetIndex; // 如果发生了reverse操作，需要还原真实的索引
                  clickImagePrompt(targetIndex, targetPrompt.prompt);
                };
                if (status === 'uploading') return <LoadingOverlay key={id} />;
                if (status === 'done' && prompt) {
                  const isActive = imageSource?.sourceImage === prompt.sourceImage && activeElementId === '';
                  return renderImageDom(
                    id,
                    prompt,
                    renderRemoveDom(
                      () => imagePrompt.prompt && handleRemoveImage?.(imagePrompt.prompt, idx),
                      total === 1,
                      isActive
                        ? total === 3 && idx !== 0
                          ? () => selectNextImagePrompt(true, idx) // 当3张图片时，删除第2或第3张时，删除后视觉上选择第2个图片
                          : () => selectNextImagePrompt(false, idx) // 其余所有情况，删除后视觉上选择第1个图片
                        : undefined,
                    ),
                    idx,
                    isActive,
                  );
                }
                return null;
              })}
            </div>
          )}

          {/* 图片+主体缩略图区域 */}
          {!isSmScreen && referencePrompts && referencePrompts.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
              {referencePrompts.map(({ prompt, status, type, id }, idx) => {
                const total = referencePrompts.length;
                const selectPromptByIndex = (targetIndex: number) => {
                  const referencePrompts = store.getState().character2videoFormParams.referencePrompts || [];
                  if (total > 0) {
                    const targetPrompt = referencePrompts[targetIndex];
                    if (!targetPrompt) return;
                    if (targetPrompt.type === 'image' && targetPrompt.prompt) {
                      clickImagePrompt(targetIndex, targetPrompt.prompt);
                    } else if (targetPrompt.type === 'element') {
                      setActiveElementId(targetPrompt.id);
                    }
                  }
                };
                // 渲染上传中
                if (status === 'uploading') return <LoadingOverlay key={id} />;

                // 渲染图片
                if (type === 'image' && prompt && status === 'done') {
                  const isActive = imageSource?.sourceImage === prompt.sourceImage && activeElementId === '';
                  return renderImageDom(
                    id,
                    prompt,
                    renderRemoveDom(
                      () => handleRemoveReference?.(id),
                      total === 1,
                      isActive ? () => selectPromptByIndex(idx === total - 1 ? total - 2 : idx) : undefined,
                    ),
                    idx,
                    isActive,
                  );
                }

                // 渲染主体
                if (type === 'element' && prompt && status !== 'forbidden') {
                  const isActive = activeElementId === id;
                  return (
                    <CustomTooltip
                      key={id}
                      content={
                        <div className="h-48 w-48">
                          <MultiImageGrid
                            images={
                              convertComponents2Assets(prompt.components || [])
                                ?.map((it) => {
                                  if (!it || 'video' in it) return null;
                                  return it.selectedImagePreview || it.sourceImagePreview;
                                })
                                .filter((url): url is string => url !== null) || []
                            }
                          />
                        </div>
                      }
                      side="bottom"
                      disableHoverableContent={true}
                    >
                      <div
                        key={id}
                        className={cn(
                          'group relative h-16 w-16 cursor-pointer rounded-6 border-2 border-system-white',
                          isActive && 'gradient-border-2 border-gradient-b-primary-dark',
                        )}
                        onClick={() => setActiveElementId(id)}
                        draggable={!!handleExchange}
                        onDragStart={(e) => onDragStart(e, idx)}
                        onDragOver={(e) => onDragOver(e)}
                        onDropCapture={(e) => onDropCapture(e, idx)}
                      >
                        <MultiImageGrid
                          images={
                            convertComponents2Assets(prompt.components)
                              ?.map((it) => {
                                if (!it || 'video' in it) return null;
                                return it.selectedImagePreview || it.sourceImagePreview;
                              })
                              .filter((url): url is string => url !== null) || []
                          }
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-0% from-black/70 to-50% to-transparent" />
                        <div className="absolute right-0 bottom-0 left-0 flex items-center gap-1 p-1">
                          <ProductLineElement
                            className={cn(
                              'flex-shrink-0 text-[#3CC9FF] text-base',
                              prompt.share_meta?.status === 'approved' && 'text-[#AB8DFF]',
                            )}
                          />
                          <div className="line-clamp-1 text-[#3CC9FF] text-xs">{prompt.name}</div>
                        </div>
                        {renderRemoveDom(
                          () => handleRemoveReference?.(id),
                          total === 1,
                          isActive ? () => selectPromptByIndex(idx === total - 1 ? total - 2 : idx) : undefined,
                        )}
                      </div>
                    </CustomTooltip>
                  );
                }
                return null;
              })}

              {/* 添加图片按钮 */}
              {onUploadImage && referencePrompts.length < maxImageAndElementCount && (
                <ImageUploader
                  className="group flex h-16 w-16 flex-col items-center justify-center gap-2 rounded-8 border-2 border-system-white24 text-white hover:border-system-white36 hover:bg-system-white08"
                  maxCount={maxImageAndElementCount - referencePrompts.length}
                  scene={scene}
                  getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
                  onChange={(file) => onUploadImage(file)}
                  allowTempMode={allowTempMode}
                >
                  <BaseLinePlus className="text-2xl opacity-80 group-hover:opacity-100" />
                  <span className="text-sm opacity-80 group-hover:opacity-100">
                    {t('create.char2VideoForm.element.addImages')}
                  </span>
                </ImageUploader>
              )}
            </div>
          )}

          {/* 比例选择区域 */}
          {!isSmScreen && (
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              {aspectRatios.map((item) => (
                <CustomTooltip key={item.value} content={selectElementTooltipContent}>
                  <div
                    className={cn(
                      'flex h-11 cursor-pointer items-center justify-center gap-2 rounded-full border border-system-white24 bg-[#0D0D1214] px-5 text-white',
                      activeElementId && 'cursor-not-allowed select-none opacity-50',
                      selectedAspect === item.value
                        ? 'gradient-border-1 border-gradient-b-primary-dark'
                        : 'hover:border-system-white36',
                    )}
                    onClick={() => handleAspectChange(item.value)}
                  >
                    <CropOptionDisplay option={item} />
                  </div>
                </CustomTooltip>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-between gap-4">
          <CustomTooltip content={selectElementTooltipContent}>
            <Button
              variant="outline"
              className="h-10 border border-system-white24 bg-[#181C23] px-4 py-2 text-sm sm:h-8 md:px-5 md:text-xs"
              onClick={handleReset}
              disabled={Boolean(activeElementId)}
            >
              <BaseLineReset className="mr-2 text-lg" />
              {t('imgCropModal.deleteLast.reset')}
            </Button>
          </CustomTooltip>
          <div className="flex gap-4">
            <Button variant="outline" className="h-10 px-6 py-2 text-sm sm:h-8 md:px-5 md:text-xs" onClick={onCancel}>
              {t('create.createForm.upload.cancel')}
            </Button>
            <Button
              variant="primary"
              className="h-10 px-6 py-2 text-sm sm:h-8 md:px-5 md:text-xs"
              onClick={handleSaveCropImage}
              disabled={saving}
            >
              {saving && <Loading className="mr-2 animate-spin text-base" />}
              {t('create.createForm.upload.confirm')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
